import {
  createMemo,
  createRoot,
  getOwner,
  Owner,
  runWithOwner,
} from "solid-js";

import UWeakMap from "@repo/utils/UWeakMap";

namespace UOwner {
  export const run = <R,>(owner: Owner | null, fn: () => R): R => {
    let errorRef: { error: any } | undefined;
    const res = runWithOwner(owner, () => {
      try {
        return fn();
      } catch (error) {
        errorRef = { error };
        throw error;
      }
    })!;
    if (errorRef) throw errorRef.error;
    return res;
  };

  export const runTracked = <R,>(owner: Owner | null, fn: () => R): R => {
    let errorRef: { error: any } | undefined;
    const res = runWithOwner(owner, () => {
      return createMemo(() => {
        try {
          return fn();
        } catch (error) {
          errorRef = { error };
          throw error;
        }
      });
    })!;
    if (errorRef) throw errorRef.error;
    return res();
  };

  export const makeDetached = () =>
    createRoot((dispose) => Object.assign(getOwner()!, { dispose }));

  const undefinedSymbol = { symbol: "undefined" };
  const onceCache = new WeakMap<Owner | typeof undefinedSymbol, Set<any>>();
  export function runOnce(owner: Owner | null, key: any, fn: () => void) {
    const keyMap = UWeakMap.getOr.lazy(
      onceCache,
      owner || undefinedSymbol,
      () => {
        if (owner)
          (owner.cleanups ||= []).push(() => {
            onceCache.delete(owner);
          });
        return new Set();
      }
    );

    if (keyMap.has(key)) return;
    keyMap.add(key);

    run(owner, fn);
  }

  export const contextStack = (owner: Owner) =>
    Reflect.ownKeys(owner.context).map((key) => owner.context[key]);
}

export default UOwner;
